import { jsx as _jsx } from "react/jsx-runtime";
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { useIsRTL } from '#pplx/frontend/i18n/useIsRTL.js';
import { SIDEBAR_DEFAULT_WIDTH, SIDEBAR_LITE_COLLAPSED_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, } from '#pplx/frontend/sidebar-sdk/sidebarResizeConstants.js';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
const COLLAPSE_DRAG_WIDTH = 130;
const KEYBOARD_RESIZE_STEP = 10;
const KEYBOARD_LARGE_RESIZE_STEP = 40;
export function SidebarResizeHandle({ width, minWidth = SIDEBAR_MIN_WIDTH, maxWidth = SIDEBAR_MAX_WIDTH, resetWidth = SIDEBAR_DEFAULT_WIDTH, hideWhenCollapsed = false, isPinned = true, onPinnedChange, onPinnedCommitted, onKeyboardCollapse, onWidthChange, onWidthPreview, onResizingChange, onWidthCommitted, }) {
    const { $t } = useIntl();
    const direction = useIsRTL() ? -1 : 1;
    const canCollapse = onPinnedChange !== undefined;
    const clampWidth = useCallback((nextWidth) => Math.min(maxWidth, Math.max(minWidth, Math.round(nextWidth))), [minWidth, maxWidth]);
    const [isResizing, setIsResizing] = useState(false);
    const [focus, setFocus] = useState(null);
    const drag = useRef(null);
    const finishDrag = useCallback((cancelled) => {
        const current = drag.current;
        if (!current)
            return;
        drag.current = null;
        setIsResizing(false);
        onResizingChange?.(false);
        if (cancelled) {
            onWidthPreview(null);
            onPinnedChange?.(current.startPinned, 'cancel');
            return;
        }
        onWidthChange(current.width);
        if (current.pinned !== current.startPinned) {
            onPinnedCommitted?.(current.pinned);
        }
        if (current.width !== current.startWidth) {
            onWidthCommitted?.(current.width, 'drag');
        }
    }, [
        onWidthChange,
        onWidthPreview,
        onPinnedChange,
        onResizingChange,
        onPinnedCommitted,
        onWidthCommitted,
    ]);
    useEffect(() => () => onWidthPreview(null), [onWidthPreview]);
    useEffect(() => {
        if (!isResizing)
            return;
        const body = document.body;
        const previousCursor = body.style.cursor;
        const previousUserSelect = body.style.userSelect;
        body.style.cursor = 'col-resize';
        body.style.userSelect = 'none';
        const cancel = () => finishDrag(true);
        window.addEventListener('blur', cancel);
        return () => {
            body.style.cursor = previousCursor;
            body.style.userSelect = previousUserSelect;
            window.removeEventListener('blur', cancel);
            onResizingChange?.(false);
        };
    }, [isResizing, finishDrag, onResizingChange]);
    const commitWidth = (nextWidth, source) => {
        if (drag.current)
            return;
        const clamped = clampWidth(nextWidth);
        if (clamped === width)
            return;
        onWidthChange(clamped);
        onWidthCommitted?.(clamped, source);
    };
    const handlePointerMove = (event) => {
        const current = drag.current;
        if (!current || current.pointerId !== event.pointerId)
            return;
        const pointerX = event.clientX * direction;
        const rawWidth = current.resizeStartWidth + pointerX - current.resizeStartX;
        if (!current.pinned) {
            if (rawWidth < minWidth)
                return;
            current.pinned = true;
            current.width = clampWidth(rawWidth);
            onResizingChange?.(true);
            onWidthPreview(current.width);
            onPinnedChange?.(true, 'preview');
            return;
        }
        if (canCollapse && rawWidth <= COLLAPSE_DRAG_WIDTH) {
            current.pinned = false;
            // Collapsing must not replace the user's saved expanded width with the minimum.
            current.width = current.startWidth;
            onResizingChange?.(false);
            onWidthPreview(current.width);
            onPinnedChange?.(false, 'preview');
            return;
        }
        const nextWidth = clampWidth(rawWidth);
        if (nextWidth === current.width)
            return;
        current.width = nextWidth;
        onResizingChange?.(true);
        onWidthPreview(nextWidth);
    };
    const handleKeyDown = (event) => {
        setFocus('keyboard');
        if (event.key === 'Escape' && drag.current) {
            // Consume Escape to cancel this resize rather than dismiss another surface.
            event.preventDefault();
            finishDrag(true);
            return;
        }
        if (drag.current)
            return;
        const arrowDelta = { ArrowLeft: -direction, ArrowRight: direction }[event.key];
        if (canCollapse &&
            arrowDelta !== undefined &&
            (!isPinned || (width === minWidth && arrowDelta < 0))) {
            event.preventDefault();
            if ((!isPinned && arrowDelta < 0) || event.repeat)
                return;
            if (!isPinned)
                commitWidth(minWidth, 'keyboard');
            else
                onKeyboardCollapse?.();
            onPinnedChange?.(!isPinned, 'preview');
            onPinnedCommitted?.(!isPinned);
            return;
        }
        if (!isPinned)
            return;
        const step = event.shiftKey ? KEYBOARD_LARGE_RESIZE_STEP : KEYBOARD_RESIZE_STEP;
        const nextWidth = {
            ArrowLeft: width - step * direction,
            ArrowRight: width + step * direction,
            Home: minWidth,
            End: maxWidth,
        }[event.key];
        if (nextWidth === undefined)
            return;
        // Arrow and boundary keys resize the pane instead of scrolling the page.
        event.preventDefault();
        commitWidth(nextWidth, 'keyboard');
    };
    // Keep keyboard focus until the collapsed shell hands it to its Expand control.
    if (hideWhenCollapsed && !isPinned && !isResizing && focus !== 'keyboard') {
        return null;
    }
    const hidden = hideWhenCollapsed && !isPinned;
    return (_jsx(BoxPrimitive, { role: "separator", "aria-orientation": "vertical", "aria-label": $t({
            defaultMessage: 'Resize sidebar',
            description: 'Accessible label for the draggable sidebar width control',
            id: '694adf1854',
        }), "aria-valuemin": isPinned ? minWidth : SIDEBAR_LITE_COLLAPSED_WIDTH, "aria-valuemax": maxWidth, "aria-valuenow": isPinned ? width : SIDEBAR_LITE_COLLAPSED_WIDTH, "aria-hidden": hidden || undefined, tabIndex: hidden ? -1 : 0, className: classNames('group absolute inset-y-0 end-0 z-20 w-1 cursor-col-resize touch-none outline-hidden', hidden && 'pointer-events-none opacity-0'), onBlur: () => setFocus(null), onPointerDown: (event) => {
            if (event.button !== 0 || drag.current)
                return;
            event.preventDefault();
            event.currentTarget.focus();
            setFocus('pointer');
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = {
                pointerId: event.pointerId,
                startWidth: width,
                startPinned: isPinned,
                resizeStartX: event.clientX * direction,
                resizeStartWidth: isPinned ? width : SIDEBAR_LITE_COLLAPSED_WIDTH,
                pinned: isPinned,
                width,
            };
            setIsResizing(true);
            onResizingChange?.(isPinned);
        }, onPointerMove: handlePointerMove, onPointerUp: (event) => {
            if (drag.current?.pointerId === event.pointerId)
                finishDrag(false);
        }, onPointerCancel: () => finishDrag(true), onLostPointerCapture: () => finishDrag(true), onDoubleClick: () => isPinned && commitWidth(resetWidth, 'reset'), onKeyDown: handleKeyDown, children: _jsx(BoxPrimitive, { className: classNames('pointer-events-none absolute inset-y-0 end-0 w-px bg-inverse group-hover:opacity-15', focus !== 'pointer' && 'group-focus-visible:opacity-40', isResizing ? 'opacity-15' : 'opacity-0') }) }));
}
//# sourceMappingURL=SidebarResizeHandle.js.map